/**
 * GameListView Component
 * 
 * List view showing routines with timeline-style task display.
 * Features Squiggle with speech bubble for empty states and inline forms.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SquiggleMascot } from '../SquiggleMascot';
import { GameSpeechBubble } from '../GameSpeechBubble';
import { RoutineCard } from './RoutineCard';
import styles from './GameListView.module.scss';

// Generic types that work with any task/routine shape
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyTask = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRoutine = any;

export interface GameListViewProps {
  routines: AnyRoutine[];
  tasks: AnyTask[];
  selectedDate: Date;
  hasAnyTasks: boolean;
  isLoading: boolean;
  onAddRoutine?: () => void;
  onTaskEdit?: (taskId: string) => void;
  onAddTask?: (routineId: string) => void;
}

export const GameListView: React.FC<GameListViewProps> = ({
  routines,
  hasAnyTasks,
  isLoading,
  onAddRoutine,
  onTaskEdit,
  onAddTask,
}) => {
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <SquiggleMascot mood="idle" size="md" />
        <p>Loading your routines...</p>
      </div>
    );
  }

  // Empty state
  if (!hasAnyTasks && routines.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyMessage}>
          you have no routines planned for today.
        </p>
        
        <div className={styles.squiggleArea}>
          <SquiggleMascot mood="winking" size="lg" />

          <GameSpeechBubble tailDirection="left">
            <button
              className={styles.startButton}
              onClick={() => onAddRoutine?.()}
              type="button"
            >
              + Create a routine
            </button>
          </GameSpeechBubble>
        </div>
      </div>
    );
  }

  // Routines list
  return (
    <div className={styles.listView}>
      <AnimatePresence>
        {routines.map((routine, index) => (
          <motion.div
            key={routine.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: index * 0.1 }}
          >
            <RoutineCard 
              routine={routine}
              onTaskEdit={onTaskEdit}
              onAddTask={() => onAddTask?.(routine.id)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default GameListView;
